import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../components/Button';
import type { OrderView } from '../../../../../services/orders/orders.service';
import classes from './guest-name-edit-modal.module.scss';
import { noSelectionTextInputProps } from '../receipt-input.utils';

// Mirrors the backend guestName schema bounds (orders.dto.ts).
const GUEST_NAME_MIN_LENGTH = 2;
const GUEST_NAME_MAX_LENGTH = 35;

export type GuestNameEditModalProps = {
    order: OrderView;
    opened: boolean;
    onClose: () => void;
    onSave: (orderId: number, guestName: string | null) => Promise<boolean>;
    isSaving: boolean;
    showCustomerNamesOnTables?: boolean;
};

const GuestNameEditModalComponent: React.FC<GuestNameEditModalProps> = ({
    order,
    opened,
    onClose,
    onSave,
    isSaving,
    showCustomerNamesOnTables = true,
}) => {
    const [draft, setDraft] = useState(order.guestName ?? '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLockRef = useRef(false);

    useEffect(() => {
        if (opened) {
            setDraft(order.guestName ?? '');
        }
    }, [opened, order.guestName]);

    const isBusy = isSaving || isSubmitting;

    const submit = useCallback(async (guestName: string | null) => {
        if (isBusy || submitLockRef.current) {
            return;
        }
        submitLockRef.current = true;
        setIsSubmitting(true);
        try {
            const didSave = await onSave(order.id, guestName);
            if (didSave) {
                onClose();
            }
        } finally {
            submitLockRef.current = false;
            setIsSubmitting(false);
        }
    }, [isBusy, onClose, onSave, order.id]);

    const handleClose = useCallback(() => {
        if (isBusy) {
            return;
        }
        setDraft(order.guestName ?? '');
        onClose();
    }, [isBusy, order.guestName, onClose]);

    const trimmedLength = draft.trim().length;
    const isTooShort = trimmedLength > 0 && trimmedLength < GUEST_NAME_MIN_LENGTH;
    const hasTable = order.tableNumber != null;

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            centered
            size={480}
            xOffset="var(--mantine-spacing-md)"
            padding={0}
            dir="rtl"
            withCloseButton={false}
            classNames={{ content: classes.modal }}
        >
            <div className={classes.header}>
                {hasTable ? (
                    <span className={classes.tableBadge} data-testid="guest-name-table-badge">{order.tableNumber}</span>
                ) : null}
                <Modal.Title className={classes.title}>שם לקוח</Modal.Title>
                <Button variant="icon" ariaLabel="סגור" className={classes.close} onClick={handleClose}
                    disabled={isBusy} dataTestId="guest-name-close">
                    <FontAwesomeIcon icon={faXmark as IconProp} />
                </Button>
            </div>
            <div className={classes.content}>
                <TextInput
                    label="שם שיוצג על השולחן"
                    placeholder="הזן שם לקוח"
                    value={draft}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    classNames={{ label: classes.label, input: classes.input }}
                    maxLength={GUEST_NAME_MAX_LENGTH}
                    data-testid="guest-name-input"
                    disabled={isBusy}
                    data-autofocus
                    {...noSelectionTextInputProps}
                />
                <div className={classes.meta}>
                    <span>{hasTable && showCustomerNamesOnTables ? 'השם מוצג במסך האזורים עד לסגירת השולחן.' : null}</span>
                    <span dir="ltr" data-testid="guest-name-counter">{draft.length}/{GUEST_NAME_MAX_LENGTH}</span>
                </div>
            </div>
            <div className={classes.footer}>
                <Button variant="confirm" className={classes.saveButton} onClick={() => submit(draft.trim() || null)}
                    disabled={isTooShort} loading={isBusy} loadingLabel="שומר..." dataTestId="guest-name-save">
                    שמור
                </Button>
                <Button variant="cancel" className={classes.cancelButton} onClick={handleClose}
                    disabled={isBusy} dataTestId="guest-name-cancel">
                    ביטול
                </Button>
                {order.guestName ? (
                    <Button variant="icon" className={classes.removeButton} onClick={() => submit(null)}
                        disabled={isBusy} dataTestId="guest-name-remove">
                        הסר שם
                    </Button>
                ) : null}
            </div>
        </Modal>
    );
};

export const GuestNameEditModal = React.memo(GuestNameEditModalComponent);
